/** Structure-aware adapter around the existing demolition damage fact. */

import type {
  DemolitionWeapon,
} from '../services/damageModel';
import type {
  DamageFact,
  DamageFactPoint,
} from '../structures/demolition/demolitionDamageLedger';
import type { ProfileId } from '../services/destructionProfiles';
import type {
  CellIdentity,
  ElementIdentity,
  FragmentIdentity,
  LayerIdentity,
  StructureIdentity,
  WorldIdentity,
} from './identities';

export interface DamageContextInput {
  readonly fact: DamageFact;
  readonly worldId: WorldIdentity;
  readonly structureId: StructureIdentity;
  readonly elementId?: ElementIdentity;
  readonly layerId?: LayerIdentity;
  readonly cellId?: CellIdentity;
  readonly fragmentId?: FragmentIdentity;
  readonly materialId: ProfileId;
  readonly weapon?: DemolitionWeapon;
}

export interface StructureDamageContext {
  readonly fact: DamageFact;
  readonly worldId: WorldIdentity;
  readonly structureId: StructureIdentity;
  readonly elementId?: ElementIdentity;
  readonly layerId?: LayerIdentity;
  readonly cellId?: CellIdentity;
  readonly fragmentId?: FragmentIdentity;
  /** Existing profile ID; physics remains owned by destructionProfiles/damageModel. */
  readonly materialId: ProfileId;
  /** Optional existing weapon preset; contact-load facts may have no weapon. */
  readonly weapon?: DemolitionWeapon;
}

function point(value: DamageFactPoint): DamageFactPoint {
  return Object.freeze({ x: value.x, y: value.y, z: value.z });
}

function copyFact(fact: DamageFact): DamageFact {
  return Object.freeze({
    ...(fact.epoch === undefined ? {} : { epoch: fact.epoch }),
    sequence: fact.sequence,
    simTime: fact.simTime,
    kind: fact.kind,
    position: point(fact.position),
    outwardNormal: point(fact.outwardNormal),
    radius: fact.radius,
    appliedEnergy: fact.appliedEnergy,
    normalizedSeverity: fact.normalizedSeverity,
    ...(fact.cellId === undefined ? {} : { cellId: fact.cellId }),
    damageBefore: fact.damageBefore,
    damageAfter: fact.damageAfter,
    topologyChanged: fact.topologyChanged,
    ...(fact.impactTangent
      ? { impactTangent: Object.freeze({ x: fact.impactTangent.x, y: fact.impactTangent.y }) }
      : {}),
    ...(fact.impactGrazing === undefined ? {} : { impactGrazing: fact.impactGrazing }),
    ...(fact.pulverizedVolume === undefined ? {} : { pulverizedVolume: fact.pulverizedVolume }),
  });
}

function assertParent(child: { readonly parent?: { readonly key: string } }, expected: { readonly key: string }, label: string): void {
  if (child.parent?.key !== expected.key) {
    throw new Error(`${label} identity is not attached to the supplied parent`);
  }
}

function assertKind(value: { readonly kind: string }, expected: string, label: string): void {
  if (value.kind !== expected) throw new TypeError(`${label} must be a ${expected} identity`);
}

/**
 * Copy and enrich one existing DamageFact.  No damage transfer function is
 * performed here; this only supplies structure/layer provenance to the fact.
 */
export function createStructureDamageContext(input: DamageContextInput): StructureDamageContext {
  assertKind(input.worldId, 'world', 'worldId');
  assertKind(input.structureId, 'structure', 'structureId');
  assertParent(input.structureId, input.worldId, 'structureId');
  if (input.elementId) {
    assertKind(input.elementId, 'element', 'elementId');
    assertParent(input.elementId, input.structureId, 'elementId');
  }
  if (input.layerId) {
    assertKind(input.layerId, 'layer', 'layerId');
    if (!input.elementId) throw new Error('layerId requires elementId');
    assertParent(input.layerId, input.elementId, 'layerId');
  }
  if (input.cellId) {
    assertKind(input.cellId, 'cell', 'cellId');
    if (!input.layerId) throw new Error('cellId requires layerId');
    assertParent(input.cellId, input.layerId, 'cellId');
  }
  if (input.fragmentId) {
    assertKind(input.fragmentId, 'fragment', 'fragmentId');
    if (!input.cellId && !input.layerId) throw new Error('fragmentId requires cellId or layerId');
    assertParent(input.fragmentId, input.cellId ?? input.layerId!, 'fragmentId');
  }

  const fact = copyFact(input.fact);
  return Object.freeze({
    fact,
    worldId: input.worldId,
    structureId: input.structureId,
    ...(input.elementId ? { elementId: input.elementId } : {}),
    ...(input.layerId ? { layerId: input.layerId } : {}),
    ...(input.cellId ? { cellId: input.cellId } : {}),
    ...(input.fragmentId ? { fragmentId: input.fragmentId } : {}),
    materialId: input.materialId,
    ...(input.weapon ? { weapon: input.weapon } : {}),
  });
}

/** Alias named for call sites that already have a ledger fact. */
export const adaptDamageFact = createStructureDamageContext;
